/**
 * safeRenderText.js
 * ──────────────────────────────────────────────────────────────────────────────
 * Final-layer protection: converts ANY value to a renderable string primitive.
 * Use this as a last line of defence inside JSX to guarantee no raw object
 * ever reaches React's renderer (which would throw "Objects are not valid as a
 * React child").
 *
 * Usage:
 *   import { safeRenderText } from '../utils/safeRenderText';
 *   <p>{safeRenderText(message.content)}</p>
 */

/**
 * Safely convert any value to a renderable string.
 *
 * @param {*} value - any value
 * @returns {string} always a string
 */
export const safeRenderText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
        return value.map(safeRenderText).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
        // Try known text fields in priority order
        const candidate =
            value.body ??
            value.content ??
            value.text ??
            value.message ??
            value.title ??
            value.value ??
            null;

        if (candidate !== null && typeof candidate !== 'object') return String(candidate);
        if (candidate !== null) return safeRenderText(candidate); // recurse once for nested object
        // Absolute last resort
        try { return JSON.stringify(value); } catch { return '[object]'; }
    }
    return String(value);
};

export default safeRenderText;
